import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { ScrollPanel } from 'primereact/scrollpanel';
import { Toast } from 'primereact/toast';
import { SubmitHandler, useForm } from 'react-hook-form';
import { markDeletedItems } from 'utils/markDeletedItems';

import { TaskStatusSchema } from '../constants/schemas';
import useTaskStatusesService from '../hooks/services/useTaskStatusesService';
import { TagType, TaskStatus } from '../types/interfaces';
import { TaskStatusProps } from '../types/props';
import { PickerTexts } from '../utils';

import '../ui/css/EmployeeCard.css';
import ConfirmationDialog from './shared/ConfirmationDialog';
import DeleteConfirmationDialog from './shared/DeleteConfirmationDialog';
import { TagsComponent } from './TagsComponent';

interface RefInterface {
  handleCancel: () => void;
}

const TaskStatusCard = React.forwardRef<RefInterface, TaskStatusProps>(
  (
    {
      taskStatusProps,
      onAdd,
      onUpdate,
      onDelete,
      closeDialog
    }: TaskStatusProps,
    ref: React.Ref<RefInterface>
  ) => {
    const {
      register,
      handleSubmit,
      watch,
      setValue,
      reset,
      setError,
      formState: { errors }
    } = useForm({
      resolver: zodResolver(TaskStatusSchema),
      defaultValues: taskStatusProps
    });
    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [closingCodes, setClosingCodes] = useState<TagType[]>([]);
    const [selectedStatus, setSelectedTypeFilter] = useState<TagType[]>(
      taskStatusProps.taskCloseCodes
    );
    const toast = useRef<Toast>(null);
    const statusName = watch('statusName');
    const taskCloseCodes = watch('taskCloseCodes');
    const businessProcessId = watch('id');
    const availableForImport = watch('availableForImport');

    const taskStatusesService = useTaskStatusesService();
    useEffect(() => {
      const serviceResult = taskStatusesService.getWithClosingCodes(
        taskStatusProps.id ? parseInt(taskStatusProps.id.toString()) : undefined
      );

      serviceResult.result.then((r) => {
        if (r.taskStatuses) {
          reset(r.taskStatuses);
          setSelectedTypeFilter(r.taskStatuses?.taskCloseCodes);
        }
        if (r.closingCodes) setClosingCodes(r.closingCodes);
      });
    }, [reset, taskStatusProps.id, taskStatusesService]);

    const unsavedChanges =
      JSON.stringify(taskStatusProps.taskCloseCodes) !==
        JSON.stringify(taskCloseCodes) ||
      taskStatusProps.statusName !== statusName;

    const onSubmit: SubmitHandler<TaskStatus> = async (data) => {
      const newData = { ...data };
      const dataId = data.id;
      let result: number | undefined = 0;
      const isIdExist =
        data && dataId !== undefined && dataId !== null && dataId !== '';
      if (onUpdate && isIdExist) {
        result = await onUpdate(dataId, newData);
      }
      if (onAdd && !isIdExist) {
        newData.id = 0;
        result = await onAdd(newData);
      }
      if (result === -1) {
        setError('statusName', {
          type: 'value',
          message:
            'Запись с таким названием уже есть в справочнике. Пожалуйста, укажите уникальное название'
        });
      } else {
        closeDialog && closeDialog();
      }
    };

    const handleCancel = () => {
      if (unsavedChanges) {
        setShowDialog(true);
      }
      if (closeDialog && !unsavedChanges) {
        closeDialog();
      }
    };

    useImperativeHandle(ref, () => {
      return {
        handleCancel
      };
    });

    const handleDelete = () => {
      setShowDeleteDialog(true);
    };

    const handleConfirmationContinue = () => {
      setShowDialog(false);
      if (closeDialog) {
        closeDialog();
      }
      // Perform navigation/close the card without saving
    };

    const handleDeleteCancellation = () => {
      setShowDeleteDialog(false);
    };

    const handleDeleteConfirmation = () => {
      if (onDelete) {
        onDelete(businessProcessId);
        closeDialog && closeDialog();
      }
      setShowDeleteDialog(false);
    };

    const handleConfirmationCancel = () => {
      setShowDialog(false);
    };

    const handleDeleteStatus = (taskId: string | number) => {
      const index = selectedStatus.findIndex(
        (tagComponent) => tagComponent.id === taskId
      );

      if (index !== -1) {
        const updatedTaskStatuses = [
          ...selectedStatus.slice(0, index),
          ...selectedStatus.slice(index + 1)
        ];
        setSelectedTypeFilter(updatedTaskStatuses);
        setValue('taskCloseCodes', updatedTaskStatuses);
        setSelectedTypeFilter(updatedTaskStatuses);
      }
    };


    return (
      <div className="dialog-tabless-card dialog-tabless-card--fixed-footer task-status-card__roles">
        <Toast ref={toast} />
        <div className=" gap-3 pb-0">
          <Divider />
          <div className="tasktype-card__column">
            <div className="flex flex-row gap-5">
              <div className=" w-full">
                <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="statusName" className="p-0">
                    Название<span className="required">*</span>:
                  </label>
                  <div className="col p-0">
                    <InputText
                      className="user-card__input w-full"
                      {...register('statusName')}
                    />
                  </div>
                </div>
                <p
                  className={`task-status-card-errors card-errors ${
                    errors.statusName ? 'text-red-500' : 'text-transparent'
                  } `}
                >
                  {errors.statusName ? errors.statusName.message : ' '}
                </p>
              </div>
            </div>

            <div className="flex flex-row gap-5">
              <div className=" w-full">
                <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <MultiSelect
                    value={taskCloseCodes}
                    options={closingCodes}
                    onChange={(e) => {
                      const selectedValues = e.value as TagType[];
                      setValue('taskCloseCodes', selectedValues);
                      setSelectedTypeFilter(selectedValues);
                    }}
                    maxSelectedLabels={3}
                    optionLabel="label"
                    placeholder={PickerTexts.closingCodes(taskCloseCodes.length)}
                    fixedPlaceholder
                    filter
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-row gap-5 dialog-card__row tag-component">
              <div className=" w-full">
                <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <Card
                    style={{ width: '100%', height: '185px', color: '#6c757d' }}
                  >
                    <ScrollPanel style={{ width: '100%', height: '150px' }}>
                      {selectedStatus.length === 0 && (
                        <p>Шифры закрытия нарядов не выбраны</p>
                      )}
                      <TagsComponent
                        initialTagsItems={markDeletedItems<TagType>(selectedStatus, closingCodes)}
                        onDelete={handleDeleteStatus} // Pass the onDelete function
                      />
                    </ScrollPanel>
                  </Card>
                </div>
              </div>
            </div>

            <div className="flex flex-row gap-5 mt-3">
              <div className=" w-full">
                <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="statusName" className="p-0">
                    Может быть выбран при импорте нарядов:
                  </label>
                  <div className="col p-0">
                    <Checkbox
                      {...register('availableForImport')}
                      checked={availableForImport ?? false}
                      onChange={(e) =>
                        setValue('availableForImport', e.checked || false)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="default-card__buttons">
          <div className="flex gap-3">
            <Button
              label="Сохранить"
              icon="pi pi-check"
              onClick={handleSubmit(onSubmit)}
            />
            <Button
              label="Удалить"
              icon="pi pi-trash"
              onClick={handleDelete}
              className="p-button-danger"
            />
            <Button
              label="Отмена"
              icon="pi pi-times"
              onClick={handleCancel}
              className="p-button-secondary"
            />
          </div>
        </div>

        <ConfirmationDialog
          visible={showDialog}
          onCancel={handleConfirmationCancel}
          onContinue={handleConfirmationContinue}
        />

        <DeleteConfirmationDialog
          visible={showDeleteDialog}
          onHide={handleDeleteCancellation}
          onConfirm={handleDeleteConfirmation}
          entityType="cтатус наряда"
          entityName={statusName}
        />
      </div>
    );
  }
);

TaskStatusCard.displayName = 'TaskStatusCard';

export default TaskStatusCard;
